import { bboxPolygon, point } from '@turf/turf';

const MAX_DIGITS_COORD = 5;
const BNG_PATTERN = /^([A-HJ-Z]{1,2})\s*((\d{2}){0,5})\s*([NS][EW])?$/;
const BNG_SQUARES = [
    'A', 'B', 'C', 'D', 'E',
    'F', 'G', 'H', 'J', 'K',
    'L', 'M', 'N', 'O', 'P',
    'Q', 'R', 'S', 'T', 'U',
    'V', 'W', 'X', 'Y', 'Z'
];

export class OSGridReference {
    gridReference;
    easting;
    northing;

    square; // The 500km or 100km square component of the grid ref
    squareDx; // Easting of the square component
    squareDy; // Northing of the square component

    digits; // The digits component of the grid ref (if present)
    quadrant; // The quadrant component of the grid ref (if present)
    precision; // Precision of the grid ref in metres

    constructor (gridRef) {
        this.#parse(gridRef);
    }

    static isValid (gridRef) {
        return BNG_PATTERN.test(gridRef);
    };

    #parse (gridRef) {
        if (!OSGridReference.isValid(gridRef)) {
            throw new Error(`Invalid BNG reference: ${gridRef}`);
        }

        this.gridReference = gridRef;

        const matchGroups = gridRef.match(BNG_PATTERN, gridRef);
        this.gridSquare = matchGroups[1];
        this.digits = matchGroups[2];
        this.quadrant = matchGroups[4];

        // Set the origin - i.e. the 'VV' square's south-west corner relative to the (false) 0,0 origin at square 'SV'
        [this.squareDx, this.squareDy] = [-1_000_000, -500_000]; // in metres

        // The 500km square offset (1st grid letter)
        let letterIdx = BNG_SQUARES.indexOf(this.gridSquare[0]); // 500km offset
        this.squareDx += (letterIdx % 5) * 500_000;
        this.squareDy += (4 - Math.floor(letterIdx / 5)) * 500_000;

        // The 500km square offset, if present (2nd grid letter)
        if (this.gridSquare.length > 1) {
            letterIdx = BNG_SQUARES.indexOf(this.gridSquare[1]); // 100km offset
            this.squareDx += (letterIdx % 5) * 100_000;
            this.squareDy += (4 - Math.floor(letterIdx / 5)) * 100_000;
        }

        // Compute precision based on number of digits provided (max is 10, i.e. 5 per axis)
        const digitsPerCoord = this.digits ? Math.floor(this.digits.length / 2) : 0;
        this.precision = Math.pow(10, MAX_DIGITS_COORD - digitsPerCoord); // precision in metres

        // Split digits into easting and northing
        const digitsDx = this.digits ? Number(this.digits.substring(0, digitsPerCoord)) * this.precision : 0;
        const digitsDy = this.digits ? Number(this.digits.substring(digitsPerCoord)) * this.precision : 0;

        // Quadrant multipliers, if quadrant is specified
        const quadrantX = this.quadrant && this.quadrant[1] === 'E' ? 1 : 0;
        const quadrantY = this.quadrant && this.quadrant[0] === 'N' ? 1 : 0;

        // Quadrant offsets
        const quadrantDx = quadrantX * Math.floor(this.precision / 2);
        const quadrantDy = quadrantY * Math.floor(this.precision / 2);

        this.easting = this.squareDx + digitsDx + quadrantDx;
        this.northing = this.squareDy + digitsDy + quadrantDy;
    }

    toString () {
        return `${this.gridReference} (easting=${this.easting}, northing=${this.northing})`;
    }

    toArray () {
        return [this.easting, this.northing];
    }

    /**
     * Return the point identified by the provided grid reference as GeoJSON
     * "Point" geometry.
     *
     * @returns {Feature<Point>}
     * @see getPointBBoxGeoJson
     */
    toGeoJson () {
        return this.#addCrs(point([this.easting, this.northing],
            this.#getGeoJsonProperties(),
            { id: this.gridReference }
        ));
    }

    /**
     * Returns a bounding box such that the point identified by the grid
     * reference lies in the centre of the bounding box.
     *
     * The dimensions of the bounding box are based on the accuracy of the
     * provided grid reference.
     *
     * @returns {(number|*)[]} Order of values: x_min, y_min, x_max, y_max
     */
    getPointBBox () {
        // Location must be in centre of extent
        const halfPrecision = Math.floor(this.precision / 2);
        return [ // xMin, yMin, xMax, yMax
            this.easting - halfPrecision, this.northing - halfPrecision,
            this.easting + halfPrecision, this.northing + halfPrecision
        ];
    }

    /**
     * Return the bounding box around the point identified by the given grid
     * reference as a GeoJSON "Polygon" geometry.
     *
     * @returns {Feature<Polygon>}
     * @see getPointBBox
     * @see toGeoJson
     */
    getPointBBoxGeoJson () {
        return this.#addCrs(bboxPolygon(this.getPointBBox(), {
            id: this.gridReference,
            properties: this.#getGeoJsonProperties()
        }));
    }

    /**
     * Returns a bounding box that represents the square or quadrant identified
     * by the grid reference.
     *
     * The dimensions of the square are based on the accuracy of the provided
     * grid reference.
     *
     * @returns {(number|*)[]} Order of values: x_min, y_min, x_max, y_max
     */
    getGridSquare () {
        const precision = this.quadrant ? Math.floor(this.precision / 2) : this.precision;
        return [ // x_min, y_min, x_max, y_max
            this.easting, this.northing,
            this.easting + precision, this.northing + precision
        ];
    }

    /**
     * Returns the bounding box that represents the square or quadrant identified
     * by the grid reference as a GeoJSON "Polygon" geometry.
     *
     * @returns {Feature<Polygon>}
     * @see getGridSquare
     */
    getGridSquareGeoJson () {
        return this.#addCrs(bboxPolygon(this.getGridSquare(), {
            id: this.gridReference,
            properties: this.#getGeoJsonProperties()
        }));
    }

    #getGeoJsonProperties () {
        return {
            displayText: `${this.gridReference}`,
            type: 'Grid Reference'
        };
    }

    #addCrs (feature) {
        feature.crs = {
            type: 'name',
            properties: {
                name: 'urn:ogc:def:crs:EPSG::27700'
            }
        };
        return feature;
    }
}

export default OSGridReference;
